import {
	getCapacitorUpdateType,
	getConfigurationForUpdateType,
	isCapacitorPlatform,
} from "@utils/specific/capacitor/helpers";
import { observable, action, runInAction, computed } from "mobx";
import CapacitorUpdaterApiService from "@api-services/capacitor/CapacitorUpdaterApiService";
import { CapacitorUpdater } from "@capgo/capacitor-updater";
import { StorageStateKeysEnum } from "@utils/storage";
import { localizationService } from "@state";
import { Capacitor } from "@capacitor/core";
import type { RootStore } from "@state/stores/types";
import CapacitorUpdatePopupStore from "@state/stores/popups/CapacitorUpdatePopupStore";
import { androidAppStoreUrl, iOSAppStoreURL } from "@utils/specific/capacitor";
import { DateTime } from "luxon";
import { logger } from "@utils/logger/logger";

export class CapacitorUpdaterStore {
	@observable isLoading = false;
	@observable declinedVersion: string | null = null;

	versionToUpdateTo: string | null = null;

	nativeUpdateClickCount: number = 0;
	cdnVersion: string | null = null;

	@computed get isPopupVisible(): boolean {
		return this.capacitorUpdatePopupStore.isPopupVisible;
	}

	@computed get shouldShowUpdateAvailable(): boolean {
		return this.declinedVersion != null;
	}

	rootStore: RootStore;
	capacitorUpdatePopupStore: CapacitorUpdatePopupStore;

	intervalCheckVersionValue: number = 600000;

	checkVersionIntervalId: ReturnType<typeof setInterval> | null = null;

	constructor(rootStore: RootStore) {
		this.rootStore = rootStore;
		this.capacitorUpdatePopupStore = new CapacitorUpdatePopupStore(this);
	}

	async onInitialize() {
		if (!isCapacitorPlatform()) {
			return;
		}

		if (WEBPACK_CAPACITOR_BLOCK_UPDATE_ENABLED) {
			return;
		}

		this.startVersionCheckInterval();
		this.checkVersionDiff();
	}

	@action.bound
	async checkVersionDiff(userForcesUpdate: boolean = false) {
		this.getDeclinedVersion();

		let newAvailableVersion;

		try {
			const response =
				await CapacitorUpdaterApiService.getCurrentMobileVersion();
			newAvailableVersion = response.version;

			this.cdnVersion = response.version;
		} catch (err) {
			console.error(
				"Cap update: something went wrong with fetching newest version",
				err
			);
		}

		if (!newAvailableVersion) {
			console.error(
				"Cap update: mobile version does not exist in fetched version.json"
			);
			return;
		}

		if (
			!userForcesUpdate &&
			this.declinedVersion &&
			this.declinedVersion == newAvailableVersion
		) {
			console.warn(
				"Cap update: User already declined update: ",
				newAvailableVersion
			);
			return;
		}

		const updateType = await getCapacitorUpdateType(newAvailableVersion);

		const updateConfiguration = getConfigurationForUpdateType(
			updateType,
			newAvailableVersion
		);

		if (updateConfiguration.shouldShowUpdate == false) {
			if (updateConfiguration.shouldAutoDownloadUpdate) {
				console.log("Cap update: Performing auto update...");

				this.removeDeclinedVersion();

				this.performAutomaticUpdate(
					updateConfiguration.versionToUpdateTo
				);
			} else {
				console.log("Cap update: Update not needed...");
			}

			return;
		}

		this.removeDeclinedVersion();

		runInAction(() => {
			this.capacitorUpdatePopupStore.isPopupVisible =
				updateConfiguration.shouldShowUpdate;
			this.capacitorUpdatePopupStore.shouldForceUpdate =
				updateConfiguration.shouldForceUpdate;
			this.capacitorUpdatePopupStore.shouldForceNativeUpdate =
				updateConfiguration.shouldForceNativeUpdate;
			this.versionToUpdateTo = updateConfiguration.versionToUpdateTo;
		});
	}

	@action.bound
	async performAutomaticUpdate(versionToUpdateTo: string | null) {
		if (!versionToUpdateTo) {
			console.log("Cap update: No version to update...");
			return;
		}

		this.startUpdate(versionToUpdateTo);
	}

	@action.bound
	getDeclinedVersion() {
		const localStorageVersion = this.rootStore.localStorageProvider.get(
			StorageStateKeysEnum.DECLINED_CAP_UPDATE
		) as string | null;
		this.declinedVersion = localStorageVersion;
	}

	@action.bound
	setDeclinedVersion() {
		this.rootStore.localStorageProvider.set(
			StorageStateKeysEnum.DECLINED_CAP_UPDATE,
			this.versionToUpdateTo
		);
		this.declinedVersion = this.versionToUpdateTo;
	}

	@action.bound
	removeDeclinedVersion() {
		this.rootStore.localStorageProvider.remove(
			StorageStateKeysEnum.DECLINED_CAP_UPDATE
		);
		this.declinedVersion = null;
	}

	@action.bound
	declineUpdate() {
		this.setDeclinedVersion();
	}

	@action.bound
	async acceptUpdate(forceNativeUpdate: boolean = false) {
		if (forceNativeUpdate) {
			this.nativeUpdateClickCount++;

			await this.logAppVersionsToSentry();

			//redirect user to store
			if (Capacitor.getPlatform() === "ios") {
				window.location.href = iOSAppStoreURL;
			} else if (Capacitor.getPlatform() === "android") {
				window.location.href = androidAppStoreUrl;
			}

			return;
		}

		if (!this.versionToUpdateTo) {
			console.error("Cap update: No version to update to");
			this.rootStore.notificationStore.error(
				localizationService.t("CAPACITOR_UPDATE.DOWNLOAD_ERROR")
			);
			return;
		}

		await this.startUpdate(this.versionToUpdateTo);
	}

	@action.bound
	async startUpdate(versionToUpdateTo: string) {
		this.isLoading = true;
		let newFetchedVersion;
		try {
			newFetchedVersion = await CapacitorUpdater.download({
				version: versionToUpdateTo,
				url: `${CapacitorUpdaterApiService.s3BucketUpdatePath}/app.zip?version=${DateTime.now().toMillis()}`,
			});
		} catch (err) {
			console.error(
				"Cap update: Something went wrong with fetching new bundle version",
				err
			);
		}

		if (!newFetchedVersion) {
			this.rootStore.notificationStore.error(
				localizationService.t("CAPACITOR_UPDATE.DOWNLOAD_ERROR")
			);
			this.isLoading = false;
			return;
		}

		try {
			// set deletes older version if new version is successfuly set, if older version is builtin, it will not be deleted
			await CapacitorUpdater.set({ id: newFetchedVersion.id });
		} catch (error) {
			console.error(
				"Cap update: Failed setting new version bundle",
				error
			);
			this.rootStore.notificationStore.error(
				localizationService.t("CAPACITOR_UPDATE.DOWNLOAD_ERROR")
			);
		}

		this.isLoading = false;
	}

	@action.bound
	startVersionCheckInterval() {
		this.disposeVersionCheckInterval();
		this.checkVersionIntervalId = setInterval(() => {
			this.checkVersionDiff();
		}, this.intervalCheckVersionValue);
	}

	@action.bound
	disposeVersionCheckInterval() {
		if (this.checkVersionIntervalId) {
			clearInterval(this.checkVersionIntervalId);
		}
		this.checkVersionIntervalId = null;
	}

	@action.bound
	onDispose() {
		this.capacitorUpdatePopupStore.onDispose();
		this.versionToUpdateTo = null;
		this.declinedVersion = null;
		this.disposeVersionCheckInterval();
	}

	async logAppVersionsToSentry() {
		try {
			if (this.nativeUpdateClickCount < 2) {
				return;
			}

			const [currentAppObject, cdnResponse] = await Promise.all([
				CapacitorUpdater.current(),
				CapacitorUpdaterApiService.getCurrentMobileVersion(),
			]);

			if (!currentAppObject || !cdnResponse?.version) {
				return;
			}

			logger.logError(
				`User is stuck in native update loop - ${DateTime.now()}`,
				{
					extra: {
						gp_data: {
							currentAppVersion: JSON.stringify(currentAppObject),
							cdnAppVersion: cdnResponse.version,
						},
					},
				}
			);

			runInAction(() => {
				this.nativeUpdateClickCount = 0;
			});
		} catch (e) {
			console.error("Failed to log to Sentry", e);
		}
	}
}
